import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Calculator, ArrowRight, ShieldCheck, Info } from "lucide-react";
import { useSEO } from "../lib/useSEO";
import { NON_OHIO_DISCLOSURE } from "../components/OhioExampleDisclosure";

const SITE_URL = "https://instabridgefunding.com";
const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) =>
  Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const fmtPrecise = (n) =>
  Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 });

// All financial math is server-computed via /api/public/calc/funding-cost —
// no client-side duplication of loan_engine.py rules.
async function fetchFundingCost({ principal, monthly_rate, months }) {
  const res = await fetch(`${API}/api/public/calc/funding-cost`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ principal, monthly_rate, months }),
  });
  if (!res.ok) throw new Error(`funding-cost API ${res.status}`);
  return res.json();
}
async function fetchPrograms() {
  const res = await fetch(`${API}/api/public/calc/programs`);
  if (!res.ok) throw new Error(`programs API ${res.status}`);
  return res.json();
}

export default function FundingCostCalculator() {
  useSEO(
    "Funding Cost Calculator | Instabridge Funding",
    "Instabridge Funding cost calculator using our exact 6-month-tier compounding model. See your payoff at every tier for the 3.1% and 3.8% programs. Numbers computed server-side from our actual contract logic.",
    { canonical: `${SITE_URL}/tools/funding-cost-calculator` },
  );

  const [advance, setAdvance] = useState(5000);
  const [ratePct, setRatePct] = useState(3.8);
  const [months, setMonths] = useState(12);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [programs, setPrograms] = useState([]);

  useEffect(() => {
    fetchPrograms()
      .then((d) => setPrograms(d.programs || []))
      .catch(() => setPrograms([
        { key: "3.1", monthly_rate: 0.031, label: "3.1% monthly", description: "Standard program" },
        { key: "3.8", monthly_rate: 0.038, label: "3.8% monthly", description: "Higher-risk program" },
      ]));
  }, []);

  // Debounced backend call on any input change.
  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(() => {
      fetchFundingCost({ principal: advance, monthly_rate: ratePct / 100, months })
        .then((r) => { if (!cancelled) { setResult(r); setError(null); } })
        .catch((e) => { if (!cancelled) setError(e.message); });
    }, 200);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [advance, ratePct, months]);

  const currentTier = result?.tiers_elapsed ?? 1;
  const payoff = result?.current_payoff ?? 0;
  const interest = result?.accrued_interest ?? 0;
  const effectiveMultiple = advance > 0 ? payoff / advance : 0;
  const sixMonthPct = result?.six_month_pct ?? 0;
  const annualPct = result?.annual_pct ?? 0;
  const cappedAtMaxTiers = !!result?.at_or_beyond_max_tier;
  const schedule = result?.schedule || [];
  const MAX_TIERS = result?.max_tiers || 6;

  return (
    <div className="min-h-screen bg-white" data-testid="funding-cost-calculator-page">
      <Navbar />
      <header className="border-b border-[#E5E7EB] bg-gradient-to-b from-[#F9FAFB] to-white">
        <div className="mx-auto max-w-5xl px-6 lg:px-10 pt-10 lg:pt-14 pb-10">
          <div className="uppercase-label mb-4">Interactive Tool · Contract-Exact Math (Server-Computed)</div>
          <h1
            className="font-display text-3xl sm:text-4xl lg:text-[3.25rem] text-[#0A2540] font-semibold tracking-tight leading-[1.1]"
            data-testid="tool-title"
          >
            Funding Cost Calculator: Explore an Ohio Funding Example
          </h1>
          <p className="mt-4 text-lg text-[#374151] leading-relaxed max-w-3xl">
            Every number below is computed by the same backend function
            (<code className="text-[13px]">loan_engine.compute_payoff</code>) that generates our Ohio payoff letters.
            Under our Ohio funding model, charges accrue in 6-month tiers, up to a 36-month ceiling.
          </p>
          <p className="mt-3 text-sm text-[#6B7280] leading-relaxed max-w-3xl" data-testid="calc-state-disclosure">
            This example uses our Ohio funding model. Actual funding charges and repayment terms vary by state, case details, and underwriting approval. Your final terms will be provided for review before you sign.
          </p>
          <p className="mt-3 text-sm text-[#374151] leading-relaxed max-w-3xl p-3 rounded-lg bg-[#FFFBEB] border border-[#FDE68A]" data-testid="calc-non-ohio-disclosure">
            <strong>Outside Ohio?</strong> {NON_OHIO_DISCLOSURE}
          </p>
        </div>
      </header>

      <div className="mx-auto max-w-5xl px-6 lg:px-10 py-12 lg:py-16">
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          {/* Inputs */}
          <section className="rounded-2xl border border-[#E5E7EB] p-8 bg-white shadow-sm" data-testid="calculator-inputs">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#0066FF] to-[#001A3D] text-white flex items-center justify-center">
                <Calculator className="w-5 h-5" />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-[#6B7280] font-medium">Inputs</div>
                <div className="font-display text-lg text-[#0A2540] font-semibold">Your Advance Terms</div>
              </div>
            </div>

            <label className="block mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-[#0A2540]">Purchase price to seller</span>
                <span className="text-sm text-[#0066FF] font-semibold">{fmt(advance)}</span>
              </div>
              <input
                type="range"
                min={500}
                max={50000}
                step={500}
                value={advance}
                onChange={(e) => setAdvance(Number(e.target.value))}
                className="w-full"
                data-testid="input-advance"
              />
              <div className="flex justify-between text-[11px] text-[#6B7280] mt-1">
                <span>$500</span>
                <span>$50,000</span>
              </div>
            </label>

            <div className="mb-6">
              <div className="text-sm font-medium text-[#0A2540] mb-2">Funding program</div>
              <div className="grid grid-cols-2 gap-2" data-testid="input-rate">
                {(programs.length > 0 ? programs : [{ key: "3.1", monthly_rate: 0.031, label: "3.1% monthly" }, { key: "3.8", monthly_rate: 0.038, label: "3.8% monthly" }]).map((p) => {
                  const pct = p.monthly_rate * 100;
                  return (
                    <button
                      type="button"
                      key={p.key}
                      onClick={() => setRatePct(pct)}
                      className={`rounded-lg border py-3 px-3 text-sm font-medium ${ratePct === pct ? "bg-[#0A2540] text-white border-[#0A2540]" : "bg-white text-[#0A2540] border-[#E5E7EB] hover:border-[#0066FF]"}`}
                      data-testid={`rate-${p.key.replace(".", "-")}`}
                    >
                      {p.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-[#6B7280] mt-2">
                Programs served from <code>/api/public/calc/programs</code>. Charged in 6-month intervals, not monthly.
              </p>
            </div>

            <label className="block mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-[#0A2540]">Months to settlement</span>
                <span className="text-sm text-[#0066FF] font-semibold">
                  {months} mo · Tier {currentTier} of {MAX_TIERS}
                </span>
              </div>
              <input
                type="range"
                min={1}
                max={48}
                step={1}
                value={months}
                onChange={(e) => setMonths(Number(e.target.value))}
                className="w-full"
                data-testid="input-months"
              />
              <div className="flex justify-between text-[11px] text-[#6B7280] mt-1">
                <span>1 mo</span>
                <span>48 mo</span>
              </div>
              {cappedAtMaxTiers && (
                <div className="mt-2 text-[11px] text-[#0066FF]" data-testid="max-tier-notice">
                  Beyond month 36 the payoff is capped — Instabridge stops accruing after tier 6.
                </div>
              )}
            </label>

            <div className="rounded-lg bg-[#F9FAFB] border border-[#E5E7EB] p-4 flex gap-3 items-start">
              <Info className="w-4 h-4 text-[#0066FF] flex-shrink-0 mt-0.5" />
              <div className="text-[12px] text-[#374151] leading-relaxed">
                <strong>6-month tier rule.</strong> Interest is charged at the start of each 6-month tier. Settling in
                month 1 or month 6 owes the same first-tier amount. This is the contractual minimum obligation.
              </div>
            </div>

            {error ? (
              <div className="mt-3 text-[12px] text-red-600" data-testid="calc-error">
                Calculator unavailable — {error}
              </div>
            ) : null}
          </section>

          {/* Results */}
          <section className="space-y-5" data-testid="calculator-results">
            <div className="rounded-2xl bg-gradient-to-br from-[#0A2540] to-[#001A3D] text-white p-8">
              <div className="uppercase-label text-[#FAD28C] mb-3">Ownership amount at settlement</div>
              <div className="font-display text-5xl font-semibold" data-testid="result-payoff">
                {fmt(payoff)}
              </div>
              <div className="mt-2 text-white/70 text-sm" data-testid="result-tier-line">
                Tier {currentTier} · Fee: {fmt(interest)} · {effectiveMultiple.toFixed(2)}× the advance
              </div>
              <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-white/60 uppercase-label mb-1">6-month fee</div>
                  <div className="font-semibold text-lg" data-testid="result-six-month-pct">
                    {sixMonthPct.toFixed(2)}%
                  </div>
                </div>
                <div>
                  <div className="text-white/60 uppercase-label mb-1">Annualized ROI</div>
                  <div className="font-semibold text-lg" data-testid="result-annual-pct">
                    {annualPct.toFixed(2)}%
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-[#E5E7EB] p-6" data-testid="result-schedule">
              <div className="uppercase-label mb-3">Contract ownership schedule (6-month tiers)</div>
              <table className="w-full text-sm">
                <thead className="text-[#6B7280] text-xs uppercase">
                  <tr>
                    <th className="text-left py-2">Tier</th>
                    <th className="text-left py-2">Ends month</th>
                    <th className="text-right py-2">Ownership amount</th>
                    <th className="text-right py-2">Fee</th>
                  </tr>
                </thead>
                <tbody>
                  {schedule.map((r) => (
                    <tr
                      key={r.tier}
                      className={`border-t border-[#F3F4F6] ${r.tier === currentTier ? "bg-[#F0F6FF]" : ""}`}
                      data-testid={`schedule-tier-${r.tier}`}
                    >
                      <td className="py-2 font-medium">Tier {r.tier}</td>
                      <td className="py-2 text-[#6B7280]">Month {r.end_month}</td>
                      <td className="py-2 text-right font-medium">{fmtPrecise(r.payoff)}</td>
                      <td className="py-2 text-right text-[#6B7280]">{fmtPrecise(r.fee)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-[11px] text-[#6B7280] mt-3">
                Ownership amount at any point in a tier equals the amount at the end of that tier — no partial-month
                proration. After Tier 6 (month 36) the payoff is fixed.
              </p>
            </div>
          </section>
        </div>

        <div className="mt-14 rounded-2xl bg-gradient-to-br from-[#0A2540] to-[#001A3D] text-white p-10">
          <div className="grid md:grid-cols-2 gap-6 items-center">
            <div>
              <div className="uppercase-label text-[#FAD28C] mb-3">Want a real number?</div>
              <h2 className="font-display text-3xl font-semibold">See what your actual case qualifies for.</h2>
              <p className="mt-3 text-white/80">
                Underwriting is free. No credit check. Decisions typically return within 24 hours of your attorney providing the case file.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 md:justify-end">
              <Link to="/apply">
                <Button
                  className="rounded-full h-12 px-7 bg-white text-[#0A2540] hover:bg-white/90 font-medium"
                  data-testid="tool-cta-apply"
                >
                  Get a real offer <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </Link>
              <Link to="/tools/settlement-distribution-calculator">
                <Button
                  variant="ghost"
                  className="rounded-full h-12 px-7 text-white hover:bg-white/10"
                  data-testid="tool-cta-cross"
                >
                  Settlement Distribution Calculator
                </Button>
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-10 grid sm:grid-cols-3 gap-5 text-sm text-[#374151]">
          <div className="flex gap-3">
            <ShieldCheck className="w-5 h-5 text-[#0066FF] flex-shrink-0" />
            <div>
              <div className="font-medium text-[#0A2540]">Non-recourse always</div>
              <div>If your case recovers nothing, you owe nothing.</div>
            </div>
          </div>
          <div className="flex gap-3">
            <Info className="w-5 h-5 text-[#0066FF] flex-shrink-0" />
            <div>
              <div className="font-medium text-[#0A2540]">No monthly payments</div>
              <div>Paid once at settlement, out of case proceeds.</div>
            </div>
          </div>
          <div className="flex gap-3">
            <Calculator className="w-5 h-5 text-[#0066FF] flex-shrink-0" />
            <div>
              <div className="font-medium text-[#0A2540]">36-month ceiling</div>
              <div>After tier 6, the ownership amount stops growing.</div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
